import type { MaybeRefOrGetter } from '#imports'
import { computed, toValue, useCoreConfig, useGameConfigurationClient, useQuery, useUserInfo } from '#imports'
import type { Game } from '@game/types'
import type { components } from '@core/types/clients/game-configuration'

export type GameSection = {
    name: string
    games: Game[]
    type: 'Horizontal' | 'Horizontal-Tall' | 'Horizontal-Taller' | 'Vertical'
    horizontalLines: number | null
}

export function useGameSection(options: { slug: MaybeRefOrGetter<string>, page?: number, enabled?: MaybeRefOrGetter<boolean>, lazy: boolean, server?: boolean }) {
    const { client } = useGameConfigurationClient()
    const { site } = useCoreConfig()
    const { isStaff } = useUserInfo()

    const { enabled = true, lazy = true, page = 1, slug, server = true } = options

    const isActive = computed(() => toValue(enabled) && toValue(slug) !== '')

    const query = useQuery<GameSection | undefined>(async ({ signal }) => {
        // `enabled` only gates the initial run, not the refetch Nuxt fires when the
        // key changes — so the disabled/empty-slug request must be stopped here.
        if (!isActive.value) return undefined

        const slugValue = toValue(slug)
        const { data } = await client.GET('/public/game-configurations/list', {
            signal,
            params: {
                query: {
                    // site.brand may be the 'unknown' fallback, outside the generated enum.
                    brand: site.brand as components['parameters']['brandQueryParameter'],
                    slug: slugValue,
                    page,
                    ...(isStaff.value && { is_staff: true }),
                },
            },
        })

        // Endpoint returns the `Game` runtime shape, looser in the generated spec (as in useGames/useGameSearch).
        const games = [...(data?.games ?? [])].sort(
            (a, b) => (a.sort_index ?? 0) - (b.sort_index ?? 0),
        ) as unknown as Game[]

        return {
            name: data?.name || slugValue,
            games,
            type: (data?.type ?? 'Horizontal') as GameSection['type'],
            horizontalLines: data?.type?.startsWith('Horizontal') && data?.horizontal_lines ? data.horizontal_lines : null
        }
    }, {
        enabled,
        lazy,
        server,
        key: () => `game:game-section:${toValue(slug)}`,
    })

    // An inactive query stays `idle`, which useQuery would report as isLoading=true —
    // a permanent skeleton for a row that's hidden anyway.
    const isLoading = computed(() => isActive.value && query.isLoading.value)

    return query.enrich({ isLoading })
}
